/**
 * @fileoverview Letterhead region selector — drag-to-select crop UI
 *
 * Shows the rendered first page of an uploaded letterhead PDF with the four
 * detected zones (header / footer / left side / right side) drawn as
 * selection rectangles, crop-tool style. Dragging a rectangle edge adjusts
 * that zone's trim; the trims are the SAME `CropTrims` deltas the slider UI
 * edits, so the two stay interchangeable (sliders remain as fine controls).
 *
 * Geometry comes from `computeAutoCropGeometry` + `selectionRectsForTrims`,
 * which mirror the crop pipeline exactly — the rectangle shown is the band
 * that will be cut. Side zones the auto-detect missed can be created by
 * dragging in from the page edge (a negative "expand" trim).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Move } from "lucide-react";
import { cn } from "@/utils/misc";
import {
  DEFAULT_GAP_FRACTION,
  MAX_EXPAND_FRACTION,
  MAX_TRIM_FRACTION,
  computeAutoCropGeometry,
  cropTrimKey,
  selectionRectsForTrims,
  trimForEdgePosition,
  type CropTrims,
  type CropZone,
  type RenderedPage,
  type TrimEdge,
  type ZoneSelectionRect,
} from "@/utils/branding/extract-letterhead-images";

const ZONE_LABEL: Record<CropZone, string> = {
  header: "Header",
  footer: "Footer",
  leftSide: "Left side",
  rightSide: "Right side",
};

const ZONE_EDGES: Record<CropZone, TrimEdge[]> = {
  header: ["top", "bottom", "left", "right"],
  footer: ["top", "bottom", "left", "right"],
  leftSide: ["top", "bottom", "right"],
  rightSide: ["top", "bottom", "left"],
};

export interface LetterheadRegionSelectorProps {
  page: RenderedPage;
  trims: CropTrims;
  /** Zones the user switched off — dimmed, no handles, skipped on save. */
  disabledZones?: Partial<Record<CropZone, boolean>>;
  /** Fires when a drag ends, with the new trim for that (zone, edge). */
  onTrimChange: (zone: CropZone, edge: TrimEdge, trimPx: number) => void;
  /**
   * Fires when a whole zone is moved (several trims change at once). When
   * omitted, each changed trim is emitted through `onTrimChange` in turn.
   */
  onTrimsChange?: (patch: Partial<CropTrims>) => void;
  /** Mirror left/right side trims while dragging (matches the toggle). */
  mirrorSides?: boolean;
  /** Rendered width in CSS px. Default 430. */
  width?: number;
}

interface DragState {
  zone: CropZone;
  /** The edge being dragged, or "move" when the whole zone is dragged. */
  edge: TrimEdge | "move";
  /** Live trims for this drag (one key for an edge, several for a move). */
  patch: Partial<CropTrims>;
  /** Pointer origin + starting trims for a move drag. */
  origin?: { x: number; y: number; trims: CropTrims };
}

export function LetterheadRegionSelector({
  page,
  trims,
  disabledZones,
  onTrimChange,
  onTrimsChange,
  mirrorSides = false,
  width = 500,
}: LetterheadRegionSelectorProps) {
  const geo = useMemo(
    () => computeAutoCropGeometry(page, DEFAULT_GAP_FRACTION),
    [page],
  );

  // Snapshot the rendered canvas once for display. The canvas itself stays
  // owned by the upload flow (released on confirm/cancel).
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    page.canvas.toBlob((blob) => {
      if (!blob || cancelled) return;
      url = URL.createObjectURL(blob);
      setPageUrl(url);
    }, "image/png");
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setPageUrl(null);
    };
  }, [page]);

  const scale = width / geo.canvasWidth;
  const height = Math.round(geo.canvasHeight * scale);

  const maxTrimPx = Math.round(geo.canvasHeight * MAX_TRIM_FRACTION);
  const maxExpandPx = Math.round(geo.canvasHeight * MAX_EXPAND_FRACTION);
  const clampTrim = (px: number) =>
    Math.max(-maxExpandPx, Math.min(maxTrimPx, Math.round(px)));

  const [drag, setDrag] = useState<DragState | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Rects rendered from the live trims (the dragged edge, plus its mirror
  // when the mirror toggle is on) so the rectangle follows the pointer.
  const displayTrims = useMemo(() => {
    if (!drag) return trims;
    const next: CropTrims = { ...trims, ...drag.patch };
    if (
      mirrorSides &&
      drag.zone === "leftSide" &&
      drag.patch.leftTrimPx !== undefined
    )
      next.rightTrimPx = drag.patch.leftTrimPx;
    if (
      mirrorSides &&
      drag.zone === "rightSide" &&
      drag.patch.rightTrimPx !== undefined
    )
      next.leftTrimPx = drag.patch.rightTrimPx;
    return next;
  }, [trims, drag, mirrorSides]);

  const rects = useMemo(
    () => selectionRectsForTrims(geo, displayTrims),
    [geo, displayTrims],
  );
  // Which zones exist in the COMMITTED trims decides which handles are
  // mounted. Deriving that from the live drag would swap the "add band"
  // affordance for a full zone mid-drag, unmounting the element that holds
  // the pointer capture and killing the drag.
  const committedRects = useMemo(
    () => selectionRectsForTrims(geo, trims),
    [geo, trims],
  );

  /** Pointer position in rendered-canvas px. */
  const canvasPos = (e: React.PointerEvent) => {
    const el = containerRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const s = rect.width / geo.canvasWidth;
    return { x: (e.clientX - rect.left) / s, y: (e.clientY - rect.top) / s };
  };

  const beginDrag =
    (zone: CropZone, edge: TrimEdge | "move") => (e: React.PointerEvent) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      if (edge === "move") {
        const pos = canvasPos(e);
        if (!pos) return;
        setDrag({
          zone,
          edge,
          patch: {},
          origin: { ...pos, trims: displayTrims },
        });
        return;
      }
      const key = cropTrimKey(zone, edge);
      setDrag({ zone, edge, patch: { [key]: displayTrims[key] ?? 0 } });
    };

  const moveDrag =
    (zone: CropZone, edge: TrimEdge | "move") => (e: React.PointerEvent) => {
      if (!drag || drag.zone !== zone || drag.edge !== edge) return;
      const pos = canvasPos(e);
      if (!pos) return;
      if (edge === "move") {
        const origin = drag.origin;
        if (!origin) return;
        const dx = pos.x - origin.x;
        const dy = pos.y - origin.y;
        const t = origin.trims;
        // Shift every edge by the same delta. Top/left trims grow with the
        // delta; bottom/right trims (measured from the far edge) shrink.
        const patch: Partial<CropTrims> =
          zone === "header"
            ? {
                headerTopTrimPx: clampTrim(t.headerTopTrimPx + dy),
                headerTrimPx: clampTrim(t.headerTrimPx - dy),
                headerLeftTrimPx: clampTrim(t.headerLeftTrimPx + dx),
                headerRightTrimPx: clampTrim(t.headerRightTrimPx - dx),
              }
            : zone === "footer"
              ? {
                  footerTrimPx: clampTrim(t.footerTrimPx + dy),
                  footerBottomTrimPx: clampTrim(t.footerBottomTrimPx - dy),
                  footerLeftTrimPx: clampTrim(t.footerLeftTrimPx + dx),
                  footerRightTrimPx: clampTrim(t.footerRightTrimPx - dx),
                }
              : zone === "leftSide"
                ? {
                    leftTrimPx: clampTrim(t.leftTrimPx - dx),
                    leftTopTrimPx: clampTrim((t.leftTopTrimPx ?? 0) + dy),
                    leftBottomTrimPx: clampTrim((t.leftBottomTrimPx ?? 0) - dy),
                  }
                : {
                    rightTrimPx: clampTrim(t.rightTrimPx + dx),
                    rightTopTrimPx: clampTrim((t.rightTopTrimPx ?? 0) + dy),
                    rightBottomTrimPx: clampTrim(
                      (t.rightBottomTrimPx ?? 0) - dy,
                    ),
                  };
        setDrag((d) => (d ? { ...d, patch } : d));
        return;
      }
      const isVertical = edge === "left" || edge === "right";
      const trimPx = clampTrim(
        trimForEdgePosition(geo, zone, edge, isVertical ? pos.x : pos.y),
      );
      const key = cropTrimKey(zone, edge);
      setDrag((d) =>
        d && d.patch[key] !== trimPx ? { ...d, patch: { [key]: trimPx } } : d,
      );
    };

  const endDrag =
    (zone: CropZone, edge: TrimEdge | "move") => (e: React.PointerEvent) => {
      if (!drag || drag.zone !== zone || drag.edge !== edge) return;
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch {
        // already released
      }
      const { patch } = drag;
      setDrag(null);
      if (edge !== "move") {
        const key = cropTrimKey(zone, edge);
        onTrimChange(zone, edge, patch[key] ?? trims[key] ?? 0);
        return;
      }
      if (Object.keys(patch).length === 0) return;
      if (onTrimsChange) {
        onTrimsChange(patch);
        return;
      }
      // Fallback: emit each changed trim in turn.
      const edgeFor: Record<keyof CropTrims, [CropZone, TrimEdge]> = {
        headerTopTrimPx: ["header", "top"],
        headerTrimPx: ["header", "bottom"],
        headerLeftTrimPx: ["header", "left"],
        headerRightTrimPx: ["header", "right"],
        footerTrimPx: ["footer", "top"],
        footerBottomTrimPx: ["footer", "bottom"],
        footerLeftTrimPx: ["footer", "left"],
        footerRightTrimPx: ["footer", "right"],
        leftTrimPx: ["leftSide", "right"],
        rightTrimPx: ["rightSide", "left"],
        leftTopTrimPx: ["leftSide", "top"],
        leftBottomTrimPx: ["leftSide", "bottom"],
        rightTopTrimPx: ["rightSide", "top"],
        rightBottomTrimPx: ["rightSide", "bottom"],
      };
      for (const [k, v] of Object.entries(patch) as Array<
        [keyof CropTrims, number]
      >) {
        const [z, ed] = edgeFor[k];
        onTrimChange(z, ed, v);
      }
    };

  const sx = (px: number) => px * scale;

  const renderZone = (
    zone: CropZone,
    rect: ZoneSelectionRect | null,
    interactive: boolean,
  ) => {
    if (!rect) return null;
    const off = !!disabledZones?.[zone];
    const disabled = off || !interactive;
    const isSide = zone === "leftSide" || zone === "rightSide";
    return (
      <div key={zone}>
        {/* Selection rectangle */}
        <div
          className={cn(
            "pointer-events-none absolute border border-dashed transition-colors",
            off
              ? "border-primary/20 bg-primary/[0.02]"
              : "border-primary/70 bg-primary/[0.08]",
            drag?.zone === zone && "border-primary bg-primary/[0.14]",
          )}
          style={{
            top: sx(rect.top),
            left: sx(rect.left),
            width: Math.max(2, sx(rect.right - rect.left)),
            height: Math.max(2, sx(rect.bottom - rect.top)),
          }}
          aria-hidden
        />
        {/* Label chip */}
        <span
          className={cn(
            "pointer-events-none absolute rounded-sm px-1 py-px text-[9px] font-medium",
            off
              ? "bg-primary/20 text-white/80"
              : "bg-primary/80 text-primary-foreground",
          )}
          style={{
            top: zone === "footer" ? undefined : sx(rect.top) + 2,
            bottom:
              zone === "footer"
                ? Math.max(0, height - sx(rect.bottom)) + 2
                : undefined,
            left: isSide
              ? zone === "leftSide"
                ? sx(rect.left) + 2
                : undefined
              : sx(rect.left) + 2,
            right:
              zone === "rightSide" ? width - sx(rect.right) + 2 : undefined,
          }}
        >
          {ZONE_LABEL[zone]}
          {disabledZones?.[zone] ? " (off)" : ""}
        </span>
        {/* Move handle — drag the whole zone to reposition it. */}
        {!disabled && (
          <button
            type="button"
            aria-label={`Drag to move the ${ZONE_LABEL[zone].toLowerCase()} selection`}
            title={`Drag to move the whole ${ZONE_LABEL[zone].toLowerCase()} selection`}
            data-testid={`region-move-${zone}`}
            onPointerDown={beginDrag(zone, "move")}
            onPointerMove={moveDrag(zone, "move")}
            onPointerUp={endDrag(zone, "move")}
            onPointerCancel={endDrag(zone, "move")}
            className={cn(
              "absolute z-[5] cursor-move touch-none bg-transparent",
              drag?.zone === zone && drag.edge === "move" && "bg-primary/10",
            )}
            style={{
              top: sx(rect.top) + 6,
              left: sx(rect.left) + 6,
              width: Math.max(0, sx(rect.right - rect.left) - 12),
              height: Math.max(0, sx(rect.bottom - rect.top) - 12),
            }}
          >
            <span
              className="pointer-events-none absolute right-1 top-1 rounded bg-white/80 p-0.5 text-primary/60 shadow-sm"
              aria-hidden
            >
              <Move className="size-3" />
            </span>
          </button>
        )}
        {/* Edge handles */}
        {!disabled &&
          ZONE_EDGES[zone].map((edge) => {
            const isVertical = edge === "left" || edge === "right";
            const isPrimary =
              (zone === "header" && edge === "bottom") ||
              (zone === "footer" && edge === "top") ||
              (zone === "leftSide" && edge === "right") ||
              (zone === "rightSide" && edge === "left");
            const style: React.CSSProperties = isVertical
              ? {
                  top: sx(rect.top),
                  height: Math.max(8, sx(rect.bottom - rect.top)),
                  left: (edge === "left" ? sx(rect.left) : sx(rect.right)) - 5,
                  width: 10,
                }
              : {
                  left: sx(rect.left),
                  width: Math.max(8, sx(rect.right - rect.left)),
                  top: (edge === "top" ? sx(rect.top) : sx(rect.bottom)) - 5,
                  height: 10,
                };
            const active = drag?.zone === zone && drag?.edge === edge;
            return (
              <button
                key={edge}
                type="button"
                aria-label={`Drag the ${ZONE_LABEL[zone].toLowerCase()} ${edge} edge`}
                title={`${ZONE_LABEL[zone]} — drag ${edge} edge`}
                data-testid={`region-handle-${zone}-${edge}`}
                onPointerDown={beginDrag(zone, edge)}
                onPointerMove={moveDrag(zone, edge)}
                onPointerUp={endDrag(zone, edge)}
                onPointerCancel={endDrag(zone, edge)}
                className={cn(
                  "absolute z-10 touch-none rounded-sm transition-colors",
                  isVertical ? "cursor-ew-resize" : "cursor-ns-resize",
                  active
                    ? "bg-primary/40"
                    : isPrimary
                      ? "bg-primary/15 hover:bg-primary/30"
                      : "bg-transparent hover:bg-primary/25",
                )}
                style={style}
              >
                {/* Grip mark on the primary edge of each zone */}
                {isPrimary && (
                  <span
                    className={cn(
                      "pointer-events-none absolute rounded-full bg-primary/80",
                      isVertical
                        ? "left-1/2 top-1/2 h-6 w-1 -translate-x-1/2 -translate-y-1/2"
                        : "left-1/2 top-1/2 h-1 w-6 -translate-x-1/2 -translate-y-1/2",
                    )}
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
      </div>
    );
  };

  /** Edge affordance for a side band the auto-detect didn't find. */
  const renderAddSideAffordance = (zone: "leftSide" | "rightSide") => {
    if (disabledZones?.[zone]) return null;
    const edge: TrimEdge = zone === "leftSide" ? "right" : "left";
    const active = drag?.zone === zone;
    return (
      <button
        key={`add-${zone}`}
        type="button"
        aria-label={`Drag to add a ${ZONE_LABEL[zone].toLowerCase()} band`}
        title={`Drag inward to add a ${ZONE_LABEL[zone].toLowerCase()} band`}
        data-testid={`region-add-${zone}`}
        onPointerDown={beginDrag(zone, edge)}
        onPointerMove={moveDrag(zone, edge)}
        onPointerUp={endDrag(zone, edge)}
        onPointerCancel={endDrag(zone, edge)}
        className={cn(
          "group absolute inset-y-0 z-10 w-3 cursor-ew-resize touch-none border-dashed border-primary/25 transition-colors hover:bg-primary/10",
          zone === "leftSide" ? "left-0 border-r" : "right-0 border-l",
          active && "bg-primary/15",
        )}
      >
        <span
          className="pointer-events-none absolute left-1/2 top-1/2 h-8 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/30 transition group-hover:bg-primary/60"
          aria-hidden
        />
      </button>
    );
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        ref={containerRef}
        className="relative shrink-0 overflow-hidden rounded border border-border/70 bg-white shadow-sm"
        style={{ width, height }}
        data-testid="letterhead-region-selector"
      >
        {pageUrl ? (
          <img
            src={pageUrl}
            alt="Rendered letterhead page"
            className="block h-full w-full select-none"
            draggable={false}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-primary/40">
            Rendering page…
          </div>
        )}
        {renderZone("header", rects.header, !!committedRects.header)}
        {renderZone("footer", rects.footer, !!committedRects.footer)}
        {renderZone("leftSide", rects.leftSide, !!committedRects.leftSide)}
        {renderZone("rightSide", rects.rightSide, !!committedRects.rightSide)}
        {!committedRects.leftSide && renderAddSideAffordance("leftSide")}
        {!committedRects.rightSide && renderAddSideAffordance("rightSide")}
      </div>
      <p
        className="text-center text-[10px] leading-snug text-primary/35"
        style={{ maxWidth: width }}
      >
        Drag a zone to move it, or drag any dashed edge to resize it. Drag in
        from the left or right page edge to add a side band. Use the fine-tune
        sliders below for pixel-level nudges.
      </p>
    </div>
  );
}
